/**
 * PromoCodesTab - Promo codes management component
 * Used in AdminFinancials for both Super Admin and Org Admin views
 */
import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Loader2, Search, Plus, Ticket, CheckCircle, XCircle,
  Globe, Users, Building2, Calendar, Edit, Trash2,
  MoreVertical, FileText
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const PromoCodesTab = ({ token, isSuperAdmin }) => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterScope, setFilterScope] = useState("all");

  const [formData, setFormData] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    scope: isSuperAdmin ? "platform" : "org",
    org_id: "",
    max_uses: "",
    max_uses_per_user: "1",
    min_purchase_amount: "",
    expiration_date: "",
    description: "",
  });

  useEffect(() => {
    fetchPromoCodes();
    if (isSuperAdmin) fetchOrganizations();
  }, [filterScope]);

  const fetchPromoCodes = async () => {
    try {
      const params = new URLSearchParams();
      if (filterScope !== "all") params.append("scope", filterScope);
      params.append("active_only", "false");

      const res = await axios.get(`${API}/promo-codes?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPromoCodes(res.data);
    } catch (error) {
      console.error("Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrganizations(res.data.items || res.data || []);
    } catch (error) {
      console.error("Failed to load organizations");
    }
  };

  const handleSubmit = async () => {
    if (!formData.code) {
      toast.error("Promo code is required");
      return;
    }
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      toast.error("Valid discount value is required");
      return;
    }

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        scope: formData.scope,
        org_id: formData.scope === "org" ? formData.org_id : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user ? parseInt(formData.max_uses_per_user) : 1,
        min_purchase_amount: formData.min_purchase_amount ? parseFloat(formData.min_purchase_amount) : null,
        expiration_date: formData.expiration_date || null,
        description: formData.description || null,
      };

      if (editingCode) {
        await axios.put(`${API}/promo-codes/${editingCode.code_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Promo code updated!");
      } else {
        await axios.post(`${API}/promo-codes`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Promo code created!");
      }

      setDialogOpen(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save promo code");
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await axios.put(
        `${API}/promo-codes/${code.code_id}`,
        { is_active: !code.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(code.is_active ? "Promo code deactivated" : "Promo code activated");
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm("Are you sure you want to deactivate this promo code?")) return;
    try {
      await axios.delete(`${API}/promo-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Promo code deactivated");
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const openEditDialog = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: code.discount_value.toString(),
      scope: code.scope,
      org_id: code.org_id || "",
      max_uses: code.max_uses?.toString() || "",
      max_uses_per_user: code.max_uses_per_user?.toString() || "1",
      min_purchase_amount: code.min_purchase_amount?.toString() || "",
      expiration_date: code.expiration_date ? code.expiration_date.split("T")[0] : "",
      description: code.description || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCode(null);
    setFormData({
      code: "",
      discount_type: "percentage",
      discount_value: "",
      scope: isSuperAdmin ? "platform" : "org",
      org_id: "",
      max_uses: "",
      max_uses_per_user: "1",
      min_purchase_amount: "",
      expiration_date: "",
      description: "",
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied!");
  };

  const getOrgName = (orgId) => {
    const org = organizations.find((o) => o.org_id === orgId);
    return org?.name || orgId;
  };

  const filteredCodes = promoCodes.filter((code) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return code.code.toLowerCase().includes(query) || code.description?.toLowerCase().includes(query);
    }
    return true;
  });

  // Separate org codes and platform codes for org admin view
  const orgCodes = filteredCodes.filter((c) => c.scope === "org");
  const platformCodes = filteredCodes.filter((c) => c.scope === "platform");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promo-codes-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Promo Codes</h3>
          <p className="text-sm text-slate-500">
            {isSuperAdmin ? "Manage platform and organization promo codes" : "Create discount codes for your programs"}
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
          data-testid="create-promo-code-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Code
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search codes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        {isSuperAdmin && (
          <Select value={filterScope} onValueChange={setFilterScope}>
            <SelectTrigger className="w-48 rounded-xl">
              <SelectValue placeholder="Filter by scope" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Codes</SelectItem>
              <SelectItem value="platform">Platform-Wide</SelectItem>
              <SelectItem value="org">Organization</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Ticket className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{isSuperAdmin ? promoCodes.length : orgCodes.length}</p>
              <p className="text-sm text-slate-500">{isSuperAdmin ? "Total" : "Your"} Codes</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {(isSuperAdmin ? promoCodes : orgCodes).filter((c) => c.is_active).length}
              </p>
              <p className="text-sm text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{platformCodes.length}</p>
              <p className="text-sm text-slate-500">Platform-Wide</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {(isSuperAdmin ? promoCodes : orgCodes).reduce((acc, c) => acc + (c.current_uses || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">Total Uses</p>
            </div>
          </div>
        </div>
      </div>

      {/* Codes Table */}
      <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Code</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Discount</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Scope</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Usage</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Status</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Expires</th>
              <th className="text-right px-6 py-3 text-sm font-medium text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCodes.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12 text-slate-500">
                  <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No promo codes found</p>
                </td>
              </tr>
            ) : (
              filteredCodes.map((code) => (
                <tr key={code.code_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-900 bg-slate-100 px-2 py-1 rounded">
                        {code.code}
                      </span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyCode(code.code)}>
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    </div>
                    {code.description && <p className="text-xs text-slate-500 mt-1">{code.description}</p>}
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-green-600">
                      {code.discount_type === "percentage" ? `${code.discount_value}% off` : `$${code.discount_value} off`}
                    </span>
                    {code.min_purchase_amount && (
                      <p className="text-xs text-slate-500">Min: ${code.min_purchase_amount}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {code.scope === "platform" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        <Globe className="w-3 h-3" />
                        Platform
                      </span>
                    ) : (
                      <div>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          <Building2 className="w-3 h-3" />
                          Org
                        </span>
                        {isSuperAdmin && <p className="text-xs text-slate-500 mt-1 truncate max-w-[120px]">{getOrgName(code.org_id)}</p>}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-900 font-medium">
                      {code.current_uses || 0}{code.max_uses && ` / ${code.max_uses}`}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {code.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        <XCircle className="w-3 h-3" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {code.expiration_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(code.expiration_date).toLocaleDateString()}
                      </div>
                    ) : (
                      <span className="text-slate-400">No expiry</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(code)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(code)}>
                          {code.is_active ? (
                            <><XCircle className="w-4 h-4 mr-2" />Deactivate</>
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-2" />Activate</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(code.code_id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle>{editingCode ? "Edit Promo Code" : "Create Promo Code"}</DialogTitle>
            <DialogDescription>
              {editingCode ? "Update promo code settings" : "Create a new discount code"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Promo Code *</Label>
              <Input
                placeholder="e.g., SUMMER20"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="mt-1 font-mono"
                disabled={!!editingCode}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type *</Label>
                <Select value={formData.discount_type} onValueChange={(v) => setFormData({ ...formData, discount_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Value *</Label>
                <Input
                  type="number"
                  placeholder={formData.discount_type === "percentage" ? "20" : "10.00"}
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  className="mt-1"
                  min="0"
                />
              </div>
            </div>

            {isSuperAdmin && (
              <>
                <div>
                  <Label>Scope *</Label>
                  <Select value={formData.scope} onValueChange={(v) => setFormData({ ...formData, scope: v, org_id: "" })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="platform">Platform-Wide</SelectItem>
                      <SelectItem value="org">Specific Organization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.scope === "org" && (
                  <div>
                    <Label>Organization *</Label>
                    <Select value={formData.org_id} onValueChange={(v) => setFormData({ ...formData, org_id: v })}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select organization" /></SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Total Uses</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Max Per User</Label>
                <Input
                  type="number"
                  placeholder="1"
                  value={formData.max_uses_per_user}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Purchase ($)</Label>
                <Input
                  type="number"
                  placeholder="No minimum"
                  value={formData.min_purchase_amount}
                  onChange={(e) => setFormData({ ...formData, min_purchase_amount: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expiration Date</Label>
                <Input
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                placeholder="Internal note"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-slate-900 hover:bg-slate-800 text-white">
              {editingCode ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodesTab;
